import { useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import { useSsoExchangeToken } from '@zep/apis';
import { useAuth } from '@zep/auth';
import { logger } from '@zep/core-logger';
import { useSsoErrorDialog } from './useSsoErrorDialog';
const DEFAULT_CALLBACK_URL = '/home/spaces';

/**
 * callbackUrl이 안전한 상대 경로인지 검증
 */
function getSafeRedirectUrl(url: string | undefined | null, fallbackUrl: string): string {
  if (!url) return fallbackUrl;
  // 상대 경로(/로 시작)만 허용, protocol-relative(//로 시작) 차단
  return url.startsWith('/') && !url.startsWith('//') ? url : fallbackUrl;
}

/**
 * SSO Exchange Token Callback View
 *
 * Auth Server에서 발급한 토큰을 ZEP 서버 인증으로 교환하는 페이지
 *
 * 토큰 전달 방식:
 * - Cross-domain: URL query parameter로 토큰 전달 (?token=xxx)
 * - Same-domain: 쿠키로 자동 전달
 *
 * 성공 시 callbackUrl 또는 /home/spaces로 리다이렉트
 */
export const SsoExchangeTokenCallbackPageView = () => {
  const router = useRouter();
  const {
    login
  } = useAuth();
  const {
    handleOpen: handleOpenSsoErrorDialog
  } = useSsoErrorDialog();
  const ssoMethod = typeof router.query.sso_method === 'string' ? router.query.sso_method : undefined;
  const callbackUrl = useMemo(() => {
    const rawCallbackUrl = typeof router.query.callbackUrl === 'string' ? router.query.callbackUrl : undefined;
    return getSafeRedirectUrl(rawCallbackUrl, DEFAULT_CALLBACK_URL);
  }, [router.query.callbackUrl]);
  const {
    mutate: exchangeToken
  } = useSsoExchangeToken({
    onSuccess: async data => {
      const {
        player
      } = data;
      await login(player);
      logger.track('login_complete', {
        ...(ssoMethod && {
          type: ssoMethod
        })
      });
      router.replace(callbackUrl);
    },
    onError: err => {
      console.error('SSO token exchange failed:', err);
      handleOpenSsoErrorDialog(err);
    }
  });
  useEffect(() => {
    if (!router.isReady) return;

    // Cross-domain 케이스: URL query에서 토큰 추출
    const token = router.query.token as string | undefined;
    exchangeToken({
      token
    });
  }, [router.isReady, router.query.token, exchangeToken]);
  return null;
};